import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Content } from '@/screens/Content';

//logos
import CarrierGuideLogo from '@/public/images/logos/carrierguide-logo.svg';

// data
const heroImages = {
  xxlarge: 'images/content/hero/hero-loadsmart-01-xxlg@2x.jpg',
  large: 'images/content/hero/hero-loadsmart-01-lg@2x.jpg',
  medium: 'images/content/hero/hero-loadsmart-01-md@2x.jpg',
  small: 'images/content/hero/hero-loadsmart-01-sm@2x.jpg',
};

const contentCopy = {
  date: `March 27, 2023`,
  title: `What is a partial truckload?`,
  slug: `what-is-partial-truckload-shipping`,
  highlight: (
    <>
      Did you know in the United States 36% of heavy-duty trucks on the road
      today run empty on average? The Bureau of Transportation Statistics
      discloses that in 2019 one out of four trucks in operation in the U.S ran
      empty, two were almost empty and one was 51% full. Loadsmart can take the
      waste out of your transportation by filling these trucks with your partial
      shipments. In the world of transportation, one size doesn’t fit all.
      Partial truckload shipments, also sometimes known as volume LTL, are too
      big for less than truckload (LTL) and too small for full truckload. When
      you have a shipment between 6 and 18 pallets or 8,000 and 28,000 pounds,
      we’re talking partials.
    </>
  ),
  copy: (
    <>
      <p>In this article, we’ll answer these key questions:</p>
      <nav>
        <ul>
          <li>
            <a href="#what-are-the-benefits-of-partial-truckload">
              What are the benefits of partial truckload?
            </a>
          </li>
          <li>
            <a href="#what-typically-makes-good-partial-freight">
              What typically makes good partial freight?
            </a>
          </li>
        </ul>
      </nav>
      <h3 id="what-are-the-benefits-of-partial-truckload">
        What are the benefits of partial truckload?
      </h3>
      <p>
        <ul>
          <li>
            No freight class is required; freight class is not required for
            partial truckload shipping, which can help you avoid extra charges
            associated with freight reclassification/overlength charges if you
            happen to get it wrong
          </li>
          <li>
            Faster transit times when needed than volume less than truckload on
            long haul freight
          </li>
          <li>
            Partial truckload can be ideal for shipments susceptible to damage
            during loading and unloading and regular cross docking with
            traditional LTL carriers that hit multiple break-bulk facilities.
          </li>
          <li>Can often provide significant savings over full truckload</li>
          <li>
            Can be paired with white-glove and final mile services typical LTL
            providers do not provide
          </li>
        </ul>
      </p>

      <h3 id="what-typically-makes-good-partial-freight">
        What typically makes good partial freight?
      </h3>
      <p>
        <ul>
          <li>
            <strong>Fragile freight:</strong> If you are worried about potential
            freight damage, a partial truck decreases the amount of handling
            necessary, thus decreasing the odds of damage during transit.
          </li>
          <li>
            <strong>Low-density freight/ High Class Freight:</strong> For a
            low-density freight, meaning a freight that is very light but takes
            up a lot of room, this shipping method would be the best choice.
          </li>
          <li>
            <strong>High Service/High Value Freight:</strong> Partial truckload
            offers the security and service protocols not typically provided by
            typical LTL providers.
          </li>
          <li>
            <strong>Seasonal or Project freight</strong> moving in between major
            metro markets and / or destined to major retailers and DC’s.
          </li>
        </ul>
      </p>
      <p>
        Loadsmart makes shipping partial truckloads simple and hassle free using
        our proprietary predictive AI Smart Match technology we match your
        partial freight with the right carrier at the right price. In fact, you
        can save as much as 15% to 25% percent by shipping a partial truckload!
      </p>
      <p>
        Reach out to your Loadsmart rep to learn more, how we can save time by
        letting us do the legwork for you, save money by leveraging one of the
        largest partial networks and only pay for the space you need and move
        more with less.
      </p>
    </>
  ),
};

const sidebarLinks = [
  {
    label:
      'Common Accounting Mistakes Trucking Companies Often Make And How To Avoid Them.',
    url: 'https://blog.loadsmart.com/2022/11/08/common-accounting-mistakes-trucking-companies-often-make-and-how-to-avoid-them',
    external: true,
  },
  {
    label:
      'Where’s My Money? Understanding the Different Ways to Pay Your Drivers.',
    url: 'https://blog.loadsmart.com/2022/11/04/wheres-my-money-understanding-the-different-ways-to-pay-your-carriers',
    external: true,
  },
  {
    label: 'How to Make More Money as a Carrier.',
    url: 'https://blog.loadsmart.com/2022/11/04/how-to-make-more-money-as-a-carrier',
    external: true,
  },
];

const bannerContent = {
  logo: <CarrierGuideLogo />,
  title: (
    <>
      Optimize dispatch.
      <br />
      Reduce overhead costs.
      <br />
      Scale trucking fleets. <br />
      All in one TMS.
    </>
  ),
  cta: 'Learn more',
  url: 'https://loadsmart.com/carrier/tms/',
  image: {
    small: '/images/content/carrierbanner/item-01-xxlarge.png',
    medium: '/images/content/carrierbanner/item-01-lg.png',
    large: '/images/content/carrierbanner/item-01-lg.png',
    xxlarge: '/images/content/carrierbanner/item-01-xxlarge.png',
  },
  alt: 'CarrierGuide dashboard screeenshots',
};

export default function WhatIsPartialTruckloadShipping() {
  return (
    <>
      <Head>
        <meta
          type="keywords"
          content="PTL Freight, partial truckload, PTL loads, what is PTL freight, partial freight loads, PTL shipping, PTL vs LTL"
        />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG === 'true'}
        noindex={process.env.NOINDEX_FLAG === 'true'}
        title="What is Partial Truckload Shipping | ShipperGuide TMS | Loadsmart"
        description="What is partial truckload or PTL for carriers looking to book and source freight loads for the logistics industry? Learn how this load is managed across the shipment's lifecycle."
        canonical="https://loadsmart.com/content/what-is-partial-truckload-shipping/"
        openGraph={{
          url: 'https://loadsmart.com/content/what-is-partial-truckload-shipping/',
        }}
      />
      <Content
        hero={heroImages}
        content={contentCopy}
        sidebar={sidebarLinks}
        banner={bannerContent}
      />
    </>
  );
}
